import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  TextField,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { useUpdateProductMutation } from '@/apis/extended/productApi';
import { parseString } from '@/utils/helper';
import { Product } from '@/types/ProductType';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';

type ProductDetailPropertySpecificsProps = {
  product: Product;
};

export const ProductDetailPropertySpecifics = ({
  product,
}: ProductDetailPropertySpecificsProps) => {
  const theme = useTheme();
  const owner = product.user;

  const [updateProduct, { isLoading: isUpdatingProduct }] =
    useUpdateProductMutation();

  const [form, setForm] = useState({
    offerPrice: '',
    hoaFees: '',
    commission: 0,
    rent: '',
    experience: '',
    rehab: '',
    arv: '',
    // removePrepaymentPenalty removed - will be handled in modal for DSCR loans only
  });

  useEffect(() => {
    setForm({
      offerPrice: parseString(product?.loanInfo?.offerPrice),
      hoaFees: parseString(product?.loanInfo?.hoaFees),
      commission: product?.loanInfo?.commission || 0,
      rent: parseString(product?.loanInfo?.rent),
      experience: parseString(product?.loanInfo?.experience),
      rehab: parseString(product?.loanInfo?.rehab),
      arv: parseString(product?.loanInfo?.arv),
      // removePrepaymentPenalty removed - will be handled in modal for DSCR loans only
    });
  }, [product]);

  const isUpdated = useMemo(() => {
    return (
      form.offerPrice !== product?.loanInfo?.offerPrice?.toString() ||
      form.hoaFees !== product?.loanInfo?.hoaFees?.toString() ||
      form.commission !== product?.loanInfo?.commission ||
      // removePrepaymentPenalty check removed - will be handled in modal for DSCR loans only
      (owner?.loanProgram === USER_LOAN_PROGRAM.DSCR &&
        form.rent !== product?.loanInfo?.rent?.toString()) ||
      (owner?.loanProgram === USER_LOAN_PROGRAM.FIX_AND_FLIP &&
        (form.experience !== product?.loanInfo?.experience?.toString() ||
          form.rehab !== product?.loanInfo?.rehab?.toString() ||
          form.arv !== product?.loanInfo?.arv?.toString()))
    );
  }, [form, product, owner?.loanProgram]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    if (name === 'commission') {
      if (Number(value) > 100) value = '100';
      if (Number(value) < 0) value = '0';
    }
    setForm({ ...form, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
  };

  const handleSave = () => {
    updateProduct({
      id: product._id,
      offerPrice: form.offerPrice ? parseInt(form.offerPrice) : undefined,
      hoaFees: form.hoaFees ? parseFloat(form.hoaFees) : undefined,
      commission: form.commission || undefined,
      rent: form.rent ? parseFloat(form.rent) : undefined,
      experience: form.experience ? parseFloat(form.experience) : undefined,
      rehab: form.rehab ? parseFloat(form.rehab) : undefined,
      arv: form.arv ? parseFloat(form.arv) : undefined,
      // removePrepaymentPenalty removed - will be handled in modal for DSCR loans only
    });
  };

  return (
    <Card
      sx={{
        border: `1px solid ${theme.palette.greyShades.border1}`,
        borderRadius: 4,
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        background: alpha(theme.palette.primary.main, 0.03),
        color: theme.palette.text.primary,
      }}
    >
      <CardHeader
        title="Property Specifics"
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        avatar={<i className="fa-light fa-landmark" aria-hidden />}
        sx={{
          '& .MuiCardHeader-title': {
            color: theme.palette.text.primary,
          },
          '& .MuiCardHeader-avatar': {
            color: theme.palette.primary.main,
            fontSize: '1.35rem',
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
      <CardContent>
        <CommonFormControl label="Offer Price:" labelWidth={230}>
          <CommonNumberInput
            disabled
            placeholder="Offer Price"
            name="offerPrice"
            value={form.offerPrice}
            onChange={handleInputChange}
          />
        </CommonFormControl>
        <CommonFormControl
          label="Monthly HOA Fees:"
          sx={{ mt: 1 }}
          labelWidth={230}
        >
          <CommonNumberInput
            disabled
            placeholder="Monthly HOA Fees"
            name="hoaFees"
            value={form.hoaFees}
            onChange={handleInputChange}
          />
        </CommonFormControl>
        <CommonFormControl
          label="Buyers Agent Commission (%):"
          sx={{ mt: 1 }}
          labelWidth={230}
        >
          <TextField
            disabled
            placeholder="Buyers Agent Commission"
            name="commission"
            InputProps={{
              endAdornment: '%',
            }}
            value={form.commission}
            onChange={handleInputChange}
          />
        </CommonFormControl>

        {owner?.loanProgram === USER_LOAN_PROGRAM.DSCR && (
          <CommonFormControl
            label="Projected Rent:"
            sx={{ mt: 1 }}
            labelWidth={230}
          >
            <CommonNumberInput
              placeholder="Projected Rent"
              name="rent"
              value={form.rent}
              onChange={handleInputChange}
            />
          </CommonFormControl>
        )}
        {owner?.loanProgram === USER_LOAN_PROGRAM.FIX_AND_FLIP && (
          <>
            <CommonFormControl label="Rehab:" sx={{ mt: 1 }} labelWidth={230}>
              <CommonNumberInput
                placeholder="Rehab"
                name="rehab"
                value={form.rehab}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              label="After Repair Value:"
              sx={{ mt: 1 }}
              labelWidth={230}
            >
              <CommonNumberInput
                placeholder="After Repair Value"
                name="arv"
                value={form.arv}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              label="Sold Flips (experience):"
              sx={{ mt: 1 }}
              labelWidth={230}
            >
              <CommonNumberInput
                placeholder="Sold Flips (experience)"
                name="experience"
                value={form.experience}
                onChange={handleInputChange}
              />
            </CommonFormControl>
          </>
        )}
        {isUpdated && (
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={isUpdatingProduct}
            >
              Save Changes
            </LoadingButton>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
